import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useFetchListData } from '@/composables'
import { STORAGE_KEYS } from '@/constants'
import { useStorage } from '@vueuse/core'
import type { Account, SocketAccountUpdatePayload } from './types'

const { SELECTED_ACCOUNT_ID } = STORAGE_KEYS

const useAccountStore = defineStore('account', () => {
  const isLoaded = ref(false)
  const loadError = ref<string | null>(null)
  const isInitializing = ref(false)
  const initializedAt = ref<number | null>(null)

  const selectedLoginid = useStorage<string | null>(SELECTED_ACCOUNT_ID, null)

  const selectedAccount = computed<Account | null>(() => {
    const id = selectedLoginid.value
    if (!id) return null
    return (accountList.value ?? []).find((account) => account.loginid == id) ?? null
  })

  const hasSelectedAccount = computed(() => !!selectedAccount.value)

  // 当前排序规则（按类型分别记忆）
  const currentSortTypeMap = ref<Record<string, string>>({
    live: 'newest',
    demo: 'newest',
    archived: 'newest',
    external: 'newest',
  })

  // 排序选项枚举
  const SORT_OPTIONS = {
    CREATE_TIME: 'create_time',
    BALANCE: 'balance',
    ACCOUNT_NAME: 'account_name',
  } as const

  // 账户分类枚举
  const ACCOUNT_CATEGORIES = {
    LIVE: 'live', // 真实账户 (real: "1", status: "1")
    DEMO: 'demo', // 模拟账户 (real: "0", status: "1")
    ARCHIVED: 'archived', // 已归档账户 (status: "0")
    REAL_UNARCHIVE: 'realUnarchive', // 真实未归档账户 (real: "1", status: "2")
  } as const

  // 供 UI 使用的排序配置（在组件中用 t(labelKey) 翻译展示）
  const sortOptions = [
    { key: 'newest', labelKey: 'account.sort.newest' },
    { key: 'oldest', labelKey: 'account.sort.oldest' },
    { key: 'balance_desc', labelKey: 'account.sort.balance_desc' },
    { key: 'balance_asc', labelKey: 'account.sort.balance_asc' },
    { key: 'name_asc', labelKey: 'account.sort.name_asc' },
    { key: 'name_desc', labelKey: 'account.sort.name_desc' },
  ] as const

  const {
    loading,
    data: accountList,
    fetchData,
  } = useFetchListData<Account[]>({
    url: '/api/exchange/opt_my_account',
    errorMessage: null,
    autoFetch: false,
    transform(data: Account[]) {
      data = data.map((item) => {
        const accountType = item.account_type
        let accountTypeName
        let canDeposit = true
        let canWithdraw = true

        if (accountType == '9') {
          // GTC信号：允许入金，不允许出金
          accountTypeName = 'account.type.gtcSignal'
          canDeposit = true
          canWithdraw = false
        } else if (accountType == '10') {
          // PAMM：不允许入金和出金
          accountTypeName = 'account.type.pamm'
          canDeposit = false
          canWithdraw = false
        } else {
          // 标准账户默认允许入金和出金
          canDeposit = true
          canWithdraw = true

          if (item.is_single == '1') {
            // 跟单信号：允许入金和出金
            accountTypeName = 'account.type.signal'
            canDeposit = true
            canWithdraw = true
          }
          if (item.is_follow == '1') {
            // 跟单账户：允许入金和出金
            accountTypeName = 'account.type.follow'
            canDeposit = true
            canWithdraw = true
          }
        }

        return { ...item, accountTypeName, canDeposit, canWithdraw }
      })

      console.log('accountList', data)

      return data
    },
  })

  const {
    loading: externalAccountLoading,
    data: externalAccountList,
    fetchData: fetchExternalAccountData,
  } = useFetchListData<Account[]>({
    url: '/api/exchange/ext_my_account',
    errorMessage: null,
    autoFetch: false,
  })

  const {
    loading: accountCategoryLoading,
    data: accountCategoryData,
    fetchData: fetchAccountCategoryData,
  } = useFetchListData<string[]>({
    url: '/api/pub/get_account_type',
    autoFetch: false,
  })

  // 简单的账户分类逻辑
  const classifyAccount = (account: Account): string => {
    // 优先按状态分类：'2' 归档，'1' 正常
    if (account.status == '2') return ACCOUNT_CATEGORIES.ARCHIVED
    // 非归档（正常）内再按 real 分类：'1' 真实，'0' 模拟
    if (account.real == '1') return ACCOUNT_CATEGORIES.LIVE
    return ACCOUNT_CATEGORIES.DEMO
  }

  // 账户排序逻辑
  const sortAccounts = (accounts: Account[], sortType: string): Account[] => {
    const sortedAccounts = [...accounts]

    const getCreateTs = (acc: Account): number => {
      const ts = Number(acc.create_time)
      if (Number.isFinite(ts) && ts > 0) return ts
      // 回退到 id，保证时间排序在缺字段时也可用
      const idNum = Number((acc as any).id)
      return Number.isFinite(idNum) ? idNum : 0
    }

    switch (sortType) {
      case SORT_OPTIONS.CREATE_TIME:
      case 'newest':
        return sortedAccounts.sort((a, b) => {
          const timeA = getCreateTs(a)
          const timeB = getCreateTs(b)
          return timeB - timeA // 降序：最新的在前
        })

      case 'oldest':
        return sortedAccounts.sort((a, b) => {
          const timeA = getCreateTs(a)
          const timeB = getCreateTs(b)
          return timeA - timeB // 升序：最旧的在前
        })

      case SORT_OPTIONS.BALANCE:
      case 'balance_desc':
        return sortedAccounts.sort((a, b) => {
          const balanceA = parseFloat(a.balance) || 0
          const balanceB = parseFloat(b.balance) || 0
          return balanceB - balanceA // 降序：余额高的在前
        })

      case 'balance_asc':
        return sortedAccounts.sort((a, b) => {
          const balanceA = parseFloat(a.balance) || 0
          const balanceB = parseFloat(b.balance) || 0
          return balanceA - balanceB // 升序：余额低的在前
        })

      case SORT_OPTIONS.ACCOUNT_NAME:
      case 'name_asc':
        return sortedAccounts.sort((a, b) => {
          const nameA = a.account_name || ''
          const nameB = b.account_name || ''
          return nameA.localeCompare(nameB) // 升序：A-Z
        })

      case 'name_desc':
        return sortedAccounts.sort((a, b) => {
          const nameA = a.account_name || ''
          const nameB = b.account_name || ''
          return nameB.localeCompare(nameA) // 降序：Z-A
        })

      default:
        return sortedAccounts
    }
  }

  const setSelectedAccount = (loginid: string) => {
    const existAccount = (accountList.value ?? []).find((a) => a.loginid == loginid)
    if (existAccount) selectedLoginid.value = loginid
  }

  const setCurrentItemNormalized = (loginid: any) => {
    setSelectedAccount(loginid)
  }

  const updateAccountInPlace = (loginid: string, patch: Record<string, any>) => {
    const target = accountList.value?.find((acc: any) => acc.loginid == loginid)
    if (target) {
      Object.assign(target, patch) // 直接修改对象属性
      if (selectedAccount.value?.loginid == loginid) {
        Object.assign(selectedAccount.value, patch)
      }
    }
  }

  const updateAccountWithSocketInfo = (payload: SocketAccountUpdatePayload) => {
    if (!payload || !payload.LOGIN) return
    updateAccountInPlace(payload.LOGIN, {
      margin: payload.MARGIN,
      credit: payload.CREDIT,
      balance: payload.BALANCE,
    })
  }

  const updateAccountWithDynamicInfo = (dynamicAccountInfo: any) => {
    const { loginid, equity, margin_fee, level_now, totalFloatingPL, totalSwaps, totalCommission } =
      dynamicAccountInfo
    updateAccountInPlace(loginid, {
      equity,
      margin_fee,
      level_now,
      totalFloatingPL,
      totalSwaps,
      totalCommission,
    })
  }

  const refreshAccounts = async () => {
    await init({ force: true, withCategory: true, withExternal: true })
  }

  const ensureSelectedAccount = () => {
    const firstAccount = accountList.value?.[0] ?? null
    if (!firstAccount) {
      selectedLoginid.value = null
      return
    }
    const valid = (accountList.value ?? []).find((a) => a.loginid == selectedLoginid.value)
    if (!valid) selectedLoginid.value = firstAccount.loginid
  }

  let _initPromise: Promise<void> | null = null
  const init = async (
    opts: { force?: boolean; withCategory?: boolean; withExternal?: boolean } = {},
  ) => {
    // 已初始化且不强制则直接返回
    if (isLoaded.value && !opts.force) return
    // 正在初始化则复用同一个 Promise，避免并发
    if (_initPromise && !opts.force) return _initPromise

    isInitializing.value = true
    loadError.value = null

    _initPromise = (async () => {
      try {
        // 基本账户数据
        const p1 = (async () => {
          await fetchData({ type: 'all', sort_type: 'newest' })
          ensureSelectedAccount()
        })()
        // 可选：并行预取分类/外部账户
        const p2 = opts.withCategory ? fetchAccountCategoryData() : Promise.resolve()
        const p3 = opts.withExternal
          ? fetchExternalAccountData({ type: 'all', sort_type: 'newest' })
          : Promise.resolve()

        const results = await Promise.allSettled([p1, p2, p3])

        // 检查是否有失败的Promise
        const failedResults = results.filter((result) => result.status == 'rejected')
        if (failedResults.length > 0) {
          throw new Error(
            `初始化失败: ${failedResults.map((r) => (r as PromiseRejectedResult).reason).join(', ')}`,
          )
        }

        initializedAt.value = Date.now()
        isLoaded.value = true
      } catch (error) {
        loadError.value = error instanceof Error ? error.message : String(error)
        console.error('Account store init failed:', error)
        throw error
      } finally {
        isInitializing.value = false
        _initPromise = null
      }
    })()

    return _initPromise
  }

  const reset = () => {
    isLoaded.value = false
    isInitializing.value = false
    loadError.value = null
    initializedAt.value = null

    loading.value = false
    accountList.value = null
    selectedLoginid.value = null

    accountCategoryData.value = null
    accountCategoryLoading.value = false
  }

  const getSortType = (category: string): string => {
    return currentSortTypeMap.value[category] ?? 'newest'
  }

  const setSortType = (category: string, sortType: string) => {
    currentSortTypeMap.value = {
      ...currentSortTypeMap.value,
      [category]: sortType,
    }
  }

  // 真实未归档账户
  const realUnarchiveAccounts = computed(() => {
    const accounts = accountList.value || []
    const filtered = accounts.filter((account) => account.status == '1' && account.real == '1')
    return sortAccounts(filtered, getSortType('realUnarchive'))
  })

  const liveAccounts = computed(() => {
    const accounts = accountList.value || []
    const filtered = accounts.filter(
      (account) => classifyAccount(account) == ACCOUNT_CATEGORIES.LIVE,
    )
    return sortAccounts(filtered, getSortType('live'))
  })

  const demoAccounts = computed(() => {
    const accounts = accountList.value || []
    const filtered = accounts.filter(
      (account) => classifyAccount(account) == ACCOUNT_CATEGORIES.DEMO,
    )
    return sortAccounts(filtered, getSortType('demo'))
  })

  const archivedAccounts = computed(() => {
    const accounts = accountList.value || []
    const filtered = accounts.filter(
      (account) => classifyAccount(account) == ACCOUNT_CATEGORIES.ARCHIVED,
    )
    return sortAccounts(filtered, getSortType('archived'))
  })

  const externalAccountsSorted = computed(() => {
    const accounts = externalAccountList.value || []
    return sortAccounts(accounts, getSortType('external'))
  })

  // 🚀 计算属性：各类账户数量（直接取length）
  const accountCounts = computed(() => ({
    live: liveAccounts.value.length,
    demo: demoAccounts.value.length,
    archived: archivedAccounts.value.length,
    total: (accountList.value || []).length,
  }))

  const accountCategoryOptions = computed(() => {
    if (!accountCategoryData.value) return []
    return accountCategoryData.value.map((item) => ({
      label: String(item),
      value: String(item),
    }))
  })

  return {
    // state
    loading,
    isLoaded,
    isInitializing,
    loadError,
    initializedAt,

    accountList,

    externalAccountList,
    externalAccountLoading,

    selectedAccount,
    selectedLoginid,

    accountCategoryLoading,
    accountCategoryData,
    accountCategoryOptions,

    // 常量
    SORT_OPTIONS,
    ACCOUNT_CATEGORIES,
    sortOptions,

    // 🚀 计算属性：实时分类账户
    realUnarchiveAccounts,
    liveAccounts,
    demoAccounts,
    archivedAccounts,
    externalAccountsSorted,
    accountCounts,
    currentSortTypeMap,
    hasSelectedAccount,

    // actions
    init,
    fetchAccountData: fetchData,
    fetchExternalAccountData,
    fetchAccountCategoryData,
    refreshAccounts,
    setSortType,
    getSortType,

    // 基础方法
    classifyAccount,
    sortAccounts,

    reset,
    setSelectedAccount,
    setCurrentItemNormalized,
    updateAccountWithDynamicInfo,
    updateAccountWithSocketInfo,
  }
})

export default useAccountStore
export { useAccountStore }
